import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLoaderData } from 'react-router-dom';
import Split from 'react-split';
import { useProjects } from '@/hooks/useProjects';
import { EditorTab, ProjectFile, FileTreeNode, Project } from '@/types';
import { filesApi, previewApi } from '@/utils/api';
import { buildFileTree, getLanguageFromPath, expandPath } from '@/utils/fileUtils';
import FileTree from '@/components/FileTree';
import EditorTabs from '@/components/EditorTabs';
import MonacoEditor from '@/components/MonacoEditor';
import PreviewPanel from '@/components/PreviewPanel';
import LoadingSpinner from '@/components/LoadingSpinner';
import { ArrowLeft, Save, Play } from 'lucide-react';

const EditorPage: React.FC = () => {
  const { project: initialProject } = useLoaderData() as { user: any; project: Project };
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { setCurrentProject } = useProjects();

  // State
  const [project] = useState<Project | null>(initialProject);
  const [, setFiles] = useState<ProjectFile[]>([]);
  const [fileTree, setFileTree] = useState<FileTreeNode[]>([]);
  const [tabs, setTabs] = useState<EditorTab[]>([]);
  const [activeTabId, setActiveTabId] = useState<string | null>(null);
  const [previewContent, setPreviewContent] = useState<{
    htmlContent?: string;
    phpUrl?: string;
    type: 'html' | 'php' | null;
  }>({ type: null });
  
  // Loading states
  const [previewLoading, setPreviewLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Modals
  const [showCreateFileModal, setShowCreateFileModal] = useState(false);
  const [createFileData, setCreateFileData] = useState<{
    parentPath: string;
    isFolder: boolean;
  } | null>(null);
  const [newFileName, setNewFileName] = useState('');

  // Load files on mount
  useEffect(() => {
    if (initialProject) {
      setCurrentProject(initialProject);
      loadFiles();
    }
  }, [initialProject, setCurrentProject]);

  const loadFiles = async () => {
    if (!initialProject) return;
    
    try {
      const filesResponse = await filesApi.getAll(initialProject.id);
      if (filesResponse.data.success) {
        const filesData = filesResponse.data.data;
        setFiles(filesData);
        setFileTree(buildFileTree(filesData));
      }
    } catch (error: any) {
      console.error('Failed to load files:', error);
    }
  };

  // Auto-save effect
  useEffect(() => {
    const interval = setInterval(() => {
      const dirtyTabs = tabs.filter(tab => tab.isDirty);
      if (dirtyTabs.length > 0) {
        dirtyTabs.forEach(tab => {
          saveFile(tab.id, false); // Silent save
        });
      }
    }, 30000); // Auto-save every 30 seconds

    return () => clearInterval(interval);
  }, [tabs]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        if (activeTabId) {
          saveFile(activeTabId);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeTabId]);


  const openFile = async (path: string) => {
    try {
      // Check if file is already open
      const existingTab = tabs.find(tab => tab.path === path);
      if (existingTab) {
        setActiveTabId(existingTab.id);
        return;
      }

      // Load file content
      const response = await filesApi.getContent(parseInt(projectId!), path);
      if (response.data.success) {
        const content = response.data.data.content;
        const fileName = path.split('/').pop() || '';
        const language = getLanguageFromPath(path);
        
        const newTab: EditorTab = {
          id: Date.now().toString(),
          name: fileName,
          path,
          content,
          isDirty: false,
          language,
        };

        setTabs(prev => [...prev, newTab]);
        setActiveTabId(newTab.id);

        // Expand path in file tree
        setFileTree(prev => expandPath(prev, path));

        // Auto-preview for HTML/PHP files
        if (language === 'html' || language === 'php') {
          refreshPreview(path);
        }
      }
    } catch (error: any) {
      console.error('Failed to open file:', error);
    }
  };

  const closeTab = (tabId: string) => {
    const tab = tabs.find(t => t.id === tabId);
    if (tab && tab.isDirty) {
      if (!window.confirm(`"${tab.name}" has unsaved changes. Close without saving?`)) {
        return;
      }
    }

    const newTabs = tabs.filter(t => t.id !== tabId);
    setTabs(newTabs);

    if (activeTabId === tabId) {
      const currentIndex = tabs.findIndex(t => t.id === tabId);
      const nextTab = newTabs[currentIndex] || newTabs[currentIndex - 1] || null;
      setActiveTabId(nextTab?.id || null);
    }
  };

  const updateTabContent = (tabId: string, content: string) => {
    setTabs(prev => prev.map(tab => 
      tab.id === tabId 
        ? { ...tab, content, isDirty: content !== tab.content }
        : tab
    ));
  };

  const saveFile = async (tabId: string, showToast = true) => {
    const tab = tabs.find(t => t.id === tabId);
    if (!tab || !tab.isDirty) return;

    try {
      setSaving(true);
      
      const response = await filesApi.update(parseInt(projectId!), tab.path, tab.content);
      if (response.data.success) {
        setTabs(prev => prev.map(t => 
          t.id === tabId ? { ...t, isDirty: false } : t
        ));

        // Refresh preview if it's the active tab and it's HTML/PHP
        if (activeTabId === tabId && (tab.language === 'html' || tab.language === 'php')) {
          refreshPreview(tab.path);
        }

        if (showToast) {
          // You could add a toast notification here
          console.log(`Saved ${tab.name}`);
        }
      }
    } catch (error: any) {
      console.error('Failed to save file:', error);
      // You could add error toast here
    } finally {
      setSaving(false);
    }
  };

  const refreshPreview = async (filePath?: string) => {
    if (!project) return;

    try {
      setPreviewLoading(true);
      
      const activeTab = tabs.find(t => t.id === activeTabId);
      const previewFile = filePath || activeTab?.path || 'index.html';
      
      const response = await previewApi.get(project.id, previewFile);
      if (response.data.success) {
        const data = response.data.data;
        setPreviewContent({
          htmlContent: data.html_content,
          phpUrl: data.php_url,
          type: data.type,
        });
      }
    } catch (error: any) {
      console.error('Failed to refresh preview:', error);
      setPreviewContent({ type: null });
    } finally {
      setPreviewLoading(false);
    }
  };

  const createFile = async (parentPath: string, isFolder: boolean) => {
    setCreateFileData({ parentPath, isFolder });
    setShowCreateFileModal(true);
  };

  const handleCreateFile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newFileName.trim() || !createFileData) return;

    try {
      const fullPath = createFileData.parentPath 
        ? `${createFileData.parentPath}/${newFileName}`
        : newFileName;

      if (createFileData.isFolder) {
        // Create folder by creating a .gitkeep file inside it
        const gitkeepPath = `${fullPath}/.gitkeep`;
        await filesApi.create(parseInt(projectId!), gitkeepPath, '.gitkeep', '');
      } else {
        await filesApi.create(parseInt(projectId!), fullPath, newFileName, '');
      }

      // Reload files
      await loadFiles();
      
      // Open the new file if it's not a folder
      if (!createFileData.isFolder) {
        openFile(fullPath);
      }

      setShowCreateFileModal(false);
      setNewFileName('');
      setCreateFileData(null);
    } catch (error: any) {
      console.error('Failed to create file:', error);
    }
  };

  const handleFileUpload = async (_parentPath: string) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    
    input.onchange = async (e) => {
      const files = (e.target as HTMLInputElement).files;
      if (!files) return;

      try {
        for (const file of Array.from(files)) {
          await filesApi.uploadAsset(parseInt(projectId!), file);
        }
        
        // Reload files
        await loadFiles();
      } catch (error: any) {
        console.error('Failed to upload files:', error);
      }
    };
    
    input.click();
  };

  if (!project) {
    return <LoadingSpinner text="Loading project..." />;
  }

  const activeTab = tabs.find(t => t.id === activeTabId);

  return (
    <div className="h-screen flex flex-col bg-gray-900">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700 px-4 py-2 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center space-x-2 text-gray-300 hover:text-white"
          >
            <ArrowLeft size={20} />
            <span>Back</span>
          </button>
          
          <div className="border-l border-gray-600 pl-4">
            <h1 className="text-lg font-semibold text-white">
              {project?.name}
            </h1>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <button
            onClick={() => activeTabId && saveFile(activeTabId)}
            disabled={!activeTab?.isDirty || saving}
            className="flex items-center space-x-2 px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save size={16} />
            <span>{saving ? 'Saving...' : 'Save'}</span>
          </button>
          
          <button
            onClick={() => refreshPreview()}
            disabled={previewLoading}
            className="flex items-center space-x-2 px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
          >
            <Play size={16} />
            <span>Preview</span>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex">
        {/* Sidebar */}
        <div className="w-64 border-r border-gray-700">
          <FileTree
            tree={fileTree}
            onFileSelect={openFile}
            onFileCreate={createFile}
            onFileUpload={handleFileUpload}
            selectedPath={activeTab?.path || null}
            onTreeUpdate={setFileTree}
          />
        </div>

        {/* Editor Area */}
        <div className="flex-1 flex flex-col">
          <EditorTabs
            tabs={tabs}
            activeTabId={activeTabId}
            onTabSelect={setActiveTabId}
            onTabClose={closeTab}
          />
          
          <div className="flex-1">
            <Split
              sizes={[50, 50]}
              minSize={300}
              direction="horizontal"
              className="flex h-full"
              gutterSize={4}
              gutterStyle={() => ({
                backgroundColor: '#374151',
                cursor: 'col-resize',
              })}
            >
              {/* Monaco Editor */}
              <div className="flex-1">
                {activeTab ? (
                  <MonacoEditor
                    value={activeTab.content}
                    language={activeTab.language}
                    onChange={(content) => updateTabContent(activeTab.id, content)}
                    path={activeTab.path}
                  />
                ) : (
                  <div className="flex items-center justify-center h-full bg-gray-900 text-gray-400">
                    <div className="text-center">
                      <div className="text-6xl mb-4">📝</div>
                      <h3 className="text-lg font-medium mb-2">No file selected</h3>
                      <p>Open a file from the sidebar to start editing</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Preview Panel */}
              <div className="flex-1 border-l border-gray-700">
                <PreviewPanel
                  htmlContent={previewContent.htmlContent}
                  phpUrl={previewContent.phpUrl}
                  type={previewContent.type}
                  onRefresh={() => refreshPreview()}
                  loading={previewLoading}
                />
              </div>
            </Split>
          </div>
        </div>
      </div>

      {/* Create File Modal */}
      {showCreateFileModal && createFileData && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3 className="text-lg font-medium text-white mb-4">
              Create {createFileData.isFolder ? 'Folder' : 'File'}
            </h3>
            
            <form onSubmit={handleCreateFile}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {createFileData.isFolder ? 'Folder' : 'File'} Name
                </label>
                <input
                  type="text"
                  value={newFileName}
                  onChange={(e) => setNewFileName(e.target.value)}
                  placeholder={createFileData.isFolder ? 'folder-name' : 'filename.ext'}
                  className="form-input"
                  autoFocus
                />
                {createFileData.parentPath && (
                  <p className="text-xs text-gray-400 mt-1">
                    Will be created in: {createFileData.parentPath}/
                  </p>
                )}
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateFileModal(false);
                    setNewFileName('');
                    setCreateFileData(null);
                  }}
                  className="form-button-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!newFileName.trim()}
                  className="form-button"
                >
                  Create {createFileData.isFolder ? 'Folder' : 'File'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditorPage;